"use client";

import { useSyncExternalStore } from "react";
import apiClient from "@/services/api-client";
import { fromContext } from "@/services/AuthorizationContext/firstLoad";

const STORAGE_KEY = "noxus.feature-flags";

let cache: Record<string, boolean> = {};
// ``loaded`` flips true only once the network fetch lands — distinct from a
// cache that is merely seeded from localStorage. Gated routes must wait for this
// before deciding a flag is off, or a hard refresh redirects out of the page.
let loaded = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

const fetchVars = async (): Promise<Record<string, boolean>> => {
  const fromLoad = await fromContext("feature_flags");
  if (fromLoad) {
    const flags: Record<string, boolean> = {};
    for (const [name, value] of Object.entries(fromLoad)) {
      if (typeof value === "boolean") flags[name] = value;
    }
    return flags;
  }
  return apiClient.get(`/api/backend/tenant/feature-flags`).then((response) => {
    return response?.data || {};
  });
};

const getVar = (name: string) => {
  return cache[name];
};

// Seed the cache from the last persisted flags (called before paint from
// providers) so a hard refresh doesn't briefly read every flag as disabled —
// which redirected gated routes and hid feature tabs until the fetch returned.
// A value already fetched this session wins over the persisted one.
const hydrateFromStorage = () => {
  if (typeof window === "undefined") return;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    cache = { ...JSON.parse(stored), ...cache };
    notify();
  } catch {
    /* ignore malformed persisted flags */
  }
};

const init = async () => {
  const result = await fetchVars();
  cache = { ...cache, ...(result || {}) };
  loaded = true;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch {
      /* ignore storage quota errors */
    }
  }
  notify();

  return cache;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export interface FeatureFlagState {
  value: boolean | undefined;
  loaded: boolean;
}

// Reactive flag access: re-renders when the cache is seeded (before paint) or the
// fetch lands. ``value`` is undefined until known; ``loaded`` is true only after
// the fetch, so callers can tell "still loading" apart from "disabled".
export function useFeatureFlag(name: string): FeatureFlagState {
  const value = useSyncExternalStore(
    subscribe,
    () => cache[name],
    () => undefined
  );
  const isLoaded = useSyncExternalStore(
    subscribe,
    () => loaded,
    () => false
  );
  return { value, loaded: isLoaded };
}

// Every flag known so far, re-rendering as they land: for callers that read
// flags named at runtime (a sidenav entry's gate).
export function useFeatureFlags(): Readonly<Record<string, boolean>> {
  return useSyncExternalStore(
    subscribe,
    () => cache,
    () => EMPTY_FLAGS
  );
}

const EMPTY_FLAGS: Readonly<Record<string, boolean>> = {};

const FFService = {
  init,
  getVar,
  hydrateFromStorage,
};

export { FFService };
